import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

// Хуки
import useAdvanceTable from 'hooks/useAdvanceTable';

// Провайдеры
import AdvanceTableProvider from 'providers/AdvanceTableProvider';

// Компоненты
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import { Badge, Button, Card, OverlayTrigger, Popover } from 'react-bootstrap';

import { ITransaction, TransactionScenarioTrigger } from './types';
import { getRiskColor } from '../../mocks/amlScenariosMocks';
import TransactionDetailModal from './TransactionDetailModal';

type TransactionsTablePropsTypes = {
  pagination: { page: number; limit: number; countAll?: number };
  transactions?: ITransaction[];
  handlePageChange: (page: number) => void;
};


const AmlTriggerBadge: React.FC<{ triggers: TransactionScenarioTrigger[] }> = ({ triggers }) => {
  const navigate = useNavigate();
  const maxRisk = Math.max(...triggers.map((t) => t.riskContribution));

  const popover = (
    <Popover style={{ maxWidth: 400 }}>
      <Popover.Header>Сработавшие AML-сценарии ({triggers.length})</Popover.Header>
      <Popover.Body className="p-2">
        {triggers.map((t) => (
          <div key={t.scenarioId} className="mb-2 pb-2 border-bottom">
            <div className="d-flex align-items-center gap-2 mb-1">
              <code style={{ fontSize: '0.7rem' }}>{t.scenarioCode}</code>
              <Badge bg={getRiskColor(t.riskContribution)} style={{ fontSize: '0.65rem' }}>
                риск {t.riskContribution}
              </Badge>
            </div>
            <div className="fw-semibold" style={{ fontSize: '0.8rem' }}>{t.scenarioName}</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t.criteriaDescription}</div>
            <Button
              variant="link"
              size="sm"
              className="p-0 mt-1"
              style={{ fontSize: '0.75rem' }}
              onClick={() => navigate('/finmonitoring')}
            >
              Перейти к сценарию →
            </Button>
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger={['click', 'focus']} placement="left" overlay={popover} rootClose>
      <Badge
        bg={getRiskColor(maxRisk)}
        style={{ cursor: 'pointer', fontSize: '0.7rem' }}
        title="Кликните для просмотра сценариев AML"
      >
        AML ×{triggers.length}
      </Badge>
    </OverlayTrigger>
  );
};

// v49 fix-8: utility — компактный показ имени по UUID.
// Backend admin-api пока не резолвит owner UUID → Name+Lastname через accounts+users
// (это P1 в бэклоге, см. CLAUDE.md v49). Здесь показываем имя если оно пришло
// в `accountOutName`/`accountInName`, иначе — короткий хвост UUID, чтобы оператор
// мог хотя бы сличить адреса визуально.
function shortAccountId(id?: string | number | null, head = 6, tail = 4): string {
  if (id === null || id === undefined || id === '') return '—';
  const str = String(id);
  if (str.length <= head + tail + 1) return str;
  return `${str.slice(0, head)}…${str.slice(-tail)}`;
}

const TransactionsTable = (props: TransactionsTablePropsTypes) => {
  const { transactions, pagination, handlePageChange } = props;
  const [preparedData, setPreparedData] = useState<ITransaction[]>([]);
  // v49 fix-7: popup с деталями транзакции при клике на trxId / строку.
  const [detailTxn, setDetailTxn] = useState<ITransaction | null>(null);

  const columns = [
    {
      accessorKey: 'trxId',
      header: 'ID Транзакции',
      meta: {
        headerProps: { className: 'text-900 ps-3 py-2' },
        cellProps: { className: 'ps-4 py-2' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => (
        <Button
          variant="link"
          size="sm"
          className="text-truncate mb-0 p-0 text-start"
          style={{ maxWidth: '160px', fontSize: '0.85rem' }}
          onClick={() => setDetailTxn(original)}
          title="Открыть детали транзакции"
        >
          {original.trxId}
        </Button>
      )
    },
    {
      accessorKey: 'accountOut',
      header: 'Отправитель',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const senderName = original.accountOutName;
        return (
          <div style={{ maxWidth: '180px' }} className="text-truncate">
            {senderName ? (
              <div>
                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{senderName}</div>
                <div className="text-muted" style={{ fontSize: '0.72rem' }} title={original.accountOut}>{shortAccountId(original.accountOut)}</div>
              </div>
            ) : (
              <span className="text-muted font-monospace" title={original.accountOut} style={{ fontSize: '0.8rem' }}>
                {shortAccountId(original.accountOut)}
              </span>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'accountIn',
      header: 'Получатель',
      meta: {
        headerProps: { className: 'text-900 ps-4' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const recipientName = original.accountInName;
        return (
          <div style={{ maxWidth: '180px' }} className="text-truncate">
            {recipientName ? (
              <div>
                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{recipientName}</div>
                <div className="text-muted" style={{ fontSize: '0.72rem' }} title={original.accountIn}>{shortAccountId(original.accountIn)}</div>
              </div>
            ) : (
              <span className="text-muted font-monospace" title={original.accountIn} style={{ fontSize: '0.8rem' }}>
                {shortAccountId(original.accountIn)}
              </span>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'amount',
      header: 'Сумма',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const amount = original.amount;
        const num = typeof amount === 'number' ? amount : parseFloat(String(amount));
        const isPositive = num > 0;
        const isNegative = num < 0;
        return (
          <span
            className={
              isPositive ? 'text-success' : isNegative ? 'text-danger' : ''
            }
          >
            {amount}
          </span>
        );
      }
    },
    {
      accessorKey: 'currency',
      header: 'Валюта',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const { currency } = original;
        return <span>{currency}</span>;
      }
    },
    {
      accessorKey: 'fee',
      header: 'Комиссия',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const { fee } = original;
        return <span>{fee}%</span>;
      }
    },
    {
      accessorKey: 'start_date',
      header: 'Время начала операции',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        return dayjs(original.start_date).format('DD.MM.YYYY HH:mm:ss');
      }
    },
    {
      accessorKey: 'update_date',
      header: 'Время обновления',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        // v49 fix-9: zero-time '0001-01-01T00:00:00Z' приходит от Go-сервиса как
        // zero value `time.Time` — означает, что транзакция ни разу не обновлялась
        // после создания (статус = same as initial). Это НЕ баг, это валидный
        // признак «не было пост-create event'ов».
        //
        // Также проверяем == start_date (некоторые backend'ы кладут created_at
        // в оба поля — тогда обновлений тоже не было).
        const ud = original.update_date;
        const isZero = !ud || ud === '0001-01-01T00:00:00Z' || ud.startsWith('0001-');
        if (isZero) return <span className="text-muted">—</span>;
        if (ud === original.start_date) return <span className="text-muted" title="Не обновлялось">—</span>;
        const d = dayjs(ud);
        return d.isValid() ? d.format('DD.MM.YYYY HH:mm:ss') : <span className="text-muted">—</span>;
      }
    },
    {
      accessorKey: 'kind',
      header: 'Статус',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const { kind } = original;
        return <span>{kind}</span>;
      }
    },
    {
      accessorKey: 'operationType',
      header: 'Тип операции',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: () => {
        return <span>Не доступно</span>; // Здесь вы можете отобразить тип операции, если данные доступны
      }
    },
    {
      accessorKey: 'description',
      header: 'Комментарий',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const { description } = original;
        return (
          <p className="text-truncate mb-0" style={{ maxWidth: '150px' }}>
            {description ? description : 'Нет комментариев'}
          </p>
        );
      }
    },
    {
      accessorKey: 'amlTriggers',
      header: 'AML',
      meta: {
        headerProps: { className: 'ps-4 text-900' },
        cellProps: { className: 'ps-4' }
      },
      cell: ({ row: { original } }: { row: { original: ITransaction } }) => {
        const triggers = original.amlTriggers;
        if (!triggers || triggers.length === 0) return <span className="text-muted">—</span>;
        return <AmlTriggerBadge triggers={triggers} />;
      }
    }
  ];

  useEffect(() => {
    if (!transactions?.length) return;
    setPreparedData(transactions);
  }, [transactions]);

  const showPagination = (pagination?.countAll ?? 0) >= 50;

  const table = useAdvanceTable({
    data: preparedData,
    columns,
    sortable: true,
    pagination: showPagination,
    perPage: pagination?.limit,
    page: pagination?.page,
    rowCount: pagination?.countAll,
    setPage: handlePageChange,
    previousPage: (page: number) => handlePageChange(page - 1),
    nextPage: (page: number) => handlePageChange(page + 1)
  } as any);

  return (
    <div className="w-100">
      <AdvanceTableProvider {...table}>
        <Card.Body className="p-0">
          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              size: 'sm',
              striped: false,
              className: 'fs-10 mb-0 overflow-hidden'
            }}
          />
        </Card.Body>
        {showPagination && (
          <Card.Footer>
            <AdvanceTablePagination />
          </Card.Footer>
        )}
      </AdvanceTableProvider>
      <TransactionDetailModal
        show={detailTxn !== null}
        transaction={detailTxn}
        onHide={() => setDetailTxn(null)}
      />
    </div>
  );
};

export default TransactionsTable;
